import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { loadCropVarietiesByCropIds } from '@next/arable-lib/crop';
import { loadCropFields, loadCropFieldsSuccess } from '@next/arable-lib/crop-field';
import { CenterOnExtent, MapContextActionTypes, MapContextState } from '@next/arable-lib/geo';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { extend } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { catchError, EMPTY, Observable, of } from 'rxjs';
import { concatMap, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';

import {
  loadSoilAnalysis,
  loadSoilAnalysisFailed,
  loadSoilAnalysisSuccess,
  refreshPageRequest,
  upsertFeatures,
} from './map.actions';
import { getFeatures } from './map.selectors';
import { SoilAnalysisApiService } from '../services/soil-analysis-api.service';
import { cropYearChanged } from '../../../../store/actions';
import { selectCropYear } from '../../../../store/selectors';

@Injectable()
export class MapEffects {
  private readonly actions$ = inject(Actions);
  private readonly soilAnalysisService = inject(SoilAnalysisApiService);
  private readonly store = inject<Store<MapContextState>>(Store);
  private platformID = inject<Object>(PLATFORM_ID);

  locationInitialized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      filter(() => isPlatformBrowser(this.platformID)),
      concatMap(() =>
        this.store.pipe(
          select(selectCropYear),
          take(1),
          concatMap((cropYear) => [loadCropFields({ cropYear, centerMap: true }), loadSoilAnalysis()]),
        ),
      ),
    ),
  );

  cropYearChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cropYearChanged),
      concatMap(({ cropYear }) => [loadCropFields({ cropYear, centerMap: true }), loadSoilAnalysis()]),
    ),
  );

  locationChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationChanged),
      concatMap(() => [refreshPageRequest()]),
    ),
  );

  refreshPage$: Observable<Observable<never>> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refreshPageRequest),
        map((_) => {
          window.location.reload();
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  loadSoilAnalysis$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSoilAnalysis),
      filter(() => isPlatformBrowser(this.platformID)),
      switchMap((_) =>
        this.store.pipe(
          select(selectCropYear),
          take(1),
          switchMap((cropYear) =>
            this.soilAnalysisService
              .fetchSoilAnalyses(cropYear)
              .pipe(map((soilAnalyses) => loadSoilAnalysisSuccess({ soilAnalyses }))),
          ),
        ),
      ),
      catchError(() => of(loadSoilAnalysisFailed({ message: 'errors.getSoilAnalysesErrorMessage' }))),
    ),
  );

  upsertFeatures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropFieldsSuccess),
      mergeMap(({ payload: { cropFields } }) => {
        const features = cropFields
          .map((cropField) =>
            cropField.feature
              ? {
                ...cropField.feature,
                cropId: cropField.cropId,
                id: cropField.id.toString(),
              }
              : null,
          )
          .filter((feature) => !!feature);

        return of(upsertFeatures(features));
      }),
    ),
  );

  loadCropVarieties$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropFieldsSuccess),
      mergeMap(({ payload: { cropFields } }) => {
        if (cropFields.length === 0) {
          return EMPTY;
        }
        const cropIds = cropFields.map((c) => c.cropId);
        return of(loadCropVarietiesByCropIds({ cropIds }));
      }),
    ),
  );

  centerOnSelection$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.CenterOnSelection),
      withLatestFrom(this.store.pipe(select(getFeatures))),
      switchMap(([_, features]) => {
        const reader = new GeoJSON();

        const featuresForExtent = features.map((feature) => reader.readFeature(feature));
        if (featuresForExtent.length === 0) {
          return EMPTY;
        }

        const extent = featuresForExtent[0].getGeometry()?.getExtent();
        if (!extent) {
          return EMPTY;
        }
        for (let index = 1; index < featuresForExtent.length; index++) {
          const featureExtend = featuresForExtent[index]?.getGeometry()?.getExtent();
          if (featureExtend) {
            extend(extent, featureExtend);
          }
        }
        return of(new CenterOnExtent(extent));
      }),
    ),
  );
}
